/**
 * Point-level 2D math and the duck-typed entity shapes the dynamic-block
 * action dispatchers share.
 *
 * ## Why this is its own module (CLAUDE.md ПРИОРИТЕТ #2)
 *
 * `geometry.ts`'s per-entity dispatchers (`translateEntity`, `stretchEntity`,
 * `rotateEntity`, `scaleEntity`, `mirrorEntity`) now delegate the three
 * newly-admitted heavy child kinds — `Polyline`, `Table`, `OLE Frame` — to
 * {@link import("./geometryPolylineTableOle")}, which reuses `command-engine`'s
 * ONE transform truth instead of restating the math. That sibling needs the
 * SAME point primitives and the SAME `EntityLike` / `CrossingFrame` shapes the
 * dispatchers use, so leaving them in `geometry.ts` would have made the two
 * modules import each other — a module CYCLE, which `.dependency-cruiser.cjs`
 * fails as an ERROR (`no-circular`).
 *
 * Hoisting the primitives into this LEAF (which imports nothing) makes the
 * graph a strict tree: `geometry.ts` → `geometryPolylineTableOle.ts` → here,
 * and `geometry.ts` → here.
 *
 * `geometry.ts` re-exports every symbol below, so no existing import path
 * changes — `from "./geometry"` and the package barrel keep resolving them.
 *
 * Pure math: no React, no drawing-model, no command-engine imports, so this
 * stays reusable on the server (DXF import validation) exactly as the original
 * `geometry.ts` header promised.
 */

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/**
 * Crossing frame stored as two arbitrary corners (AutoCAD STRETCH
 * semantics — the user draws a rectangle with any two corners, we
 * normalise to AABB for the point-in-box test). Matches the schema
 * shape used by DYN-1.1 (`{ corner1, corner2 }`).
 */
export interface CrossingFrame {
  readonly corner1: Vec2;
  readonly corner2: Vec2;
}

export type EntityLike = Record<string, unknown> & { readonly type: string };

/* ── Point-level helpers ────────────────────────────────────────── */

export function translatePoint(p: Vec2, delta: Vec2): Vec2 {
  return { x: p.x + delta.x, y: p.y + delta.y };
}

/** Inclusive AABB containment test against a two-corner crossing frame. */
export function pointInFrame(p: Vec2, frame: CrossingFrame): boolean {
  const minX = Math.min(frame.corner1.x, frame.corner2.x);
  const maxX = Math.max(frame.corner1.x, frame.corner2.x);
  const minY = Math.min(frame.corner1.y, frame.corner2.y);
  const maxY = Math.max(frame.corner1.y, frame.corner2.y);
  return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
}

/**
 * Reflect point `p` through the line defined by two points `a` and `b`.
 * Formula: p' = 2·proj_line(p) - p, using the standard projection onto
 * the line direction vector (b - a).
 *
 * Degenerate axis (a === b) is a no-op — returns p unchanged. Real DXF
 * flip axes never coincide but defensive guard is cheap.
 */
export function reflectPoint(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-18) return p;
  // Param t along the line from a where the foot of perpendicular lies.
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  const footX = a.x + t * dx;
  const footY = a.y + t * dy;
  return {
    x: 2 * footX - p.x,
    y: 2 * footY - p.y,
  };
}

/**
 * Rotate a single 2D point around `center` by `angleRad` radians (CCW).
 */
export function rotatePoint(p: Vec2, center: Vec2, angleRad: number): Vec2 {
  if (angleRad === 0) return p;
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return {
    x: center.x + dx * c - dy * s,
    y: center.y + dx * s + dy * c,
  };
}

/**
 * Scale a single 2D point about `center` with independent X / Y factors.
 */
export function scalePoint(
  p: Vec2,
  center: Vec2,
  sx: number,
  sy: number,
): Vec2 {
  if (sx === 1 && sy === 1) return p;
  return {
    x: center.x + (p.x - center.x) * sx,
    y: center.y + (p.y - center.y) * sy,
  };
}
